import { createSelector } from 'reselect';
import { Map, List } from 'immutable';
import { selectMenuTypeId } from 'selectors/browse';
import { selectOrderFulfilmentMethodId, selectOrderTotal, selectOrderCovers } from 'selectors/order';

interface Charge extends Map<string, any> {
  get(key: 'id'): number;
  get(key: 'name'): string;
  get(key: 'allow_opt_out'): boolean;
  get(key: 'charge_rule'): string | null;
  get(key: 'charge_rule_value'): number | null;
  get(key: 'service_fulfilment_methods'): List<any> | undefined;
  get(key: 'menu_types'): List<any> | undefined;
}

export const selectChargesState = (state: any) => state.get('charges');

export const selectChargesData = createSelector(
  [selectChargesState],
  (chargesState): List<Charge> => chargesState.get('data', List())
);

export const selectChargesLoading = createSelector([selectChargesState], (chargesState): boolean =>
  chargesState.get('loading', false)
);

export const selectChargesError = createSelector([selectChargesState], (chargesState): any =>
  chargesState.get('error', null)
);

export const selectHasFetchedCharges = createSelector([selectChargesState], (chargesState): boolean => {
  const data = chargesState.get('data', []);
  const loading = chargesState.get('loading', false);
  const error = chargesState.get('error', null);

  // Consider charges fetched if we have data and we're not loading and there's no error
  return data.size > 0 && !loading && !error;
});

export const selectChargeById = createSelector(
  [selectChargesData, (state: any, chargeId: number) => chargeId],
  (charges, chargeId): Charge | undefined => charges.find(charge => charge.get('id') === chargeId)
);

export const selectChargesWithOptOut = createSelector(
  [selectChargesData],
  (charges): List<Charge> => charges.filter(charge => charge.get('allow_opt_out') === true)
);

export const selectOptOutChargesThatMatchConditions = createSelector(
  [selectChargesData, selectMenuTypeId, selectOrderFulfilmentMethodId, selectOrderTotal, selectOrderCovers],
  (charges, menuTypeId, fulfilmentMethodId, orderTotal, orderCovers): List<Charge> => {
    if (!menuTypeId || !fulfilmentMethodId) {
      return List();
    }

    return charges.filter(charge => {
      if (charge.get('allow_opt_out') !== true) {
        return false;
      }

      const serviceFulfilmentMethods = charge.get('service_fulfilment_methods');
      const menuTypes = charge.get('menu_types');
      const chargeRule = charge.get('charge_rule');
      const chargeRuleValue = charge.get('charge_rule_value');
      if (chargeRule && chargeRuleValue !== null) {
        const ruleValue = chargeRuleValue;

        switch (chargeRule) {
          case 'order total LT':
            if (orderTotal > ruleValue) {
              return false;
            }
            break;
          case 'order total GT':
            if (orderTotal <= ruleValue) {
              return false;
            }
            break;
          case 'order covers GTE':
            if (orderCovers < ruleValue) {
              return false;
            }
            break;
          default:
            return false;
        }
      }

      if (serviceFulfilmentMethods.size === 0 && menuTypes.size === 0) {
        return true;
      }

      let fulfilmentMethodMatches = true;
      if (serviceFulfilmentMethods.size > 0) {
        fulfilmentMethodMatches = serviceFulfilmentMethods.some(
          (sfm: any) => sfm.get('id') === fulfilmentMethodId
        );
      }

      let menuTypeMatches = true;
      if (menuTypes.size > 0) {
        menuTypeMatches = menuTypes.some((menuType: any) => menuType.get('id') === menuTypeId);
      }

      return fulfilmentMethodMatches && menuTypeMatches;
    });
  }
);
